{
  const axiosErrorOf = (error: unknown): RollbarAxiosError | undefined => {
    let current = error as RollbarAxiosError | null | undefined
    while (current != null && !current.isAxiosError) current = current.cause as RollbarAxiosError | null | undefined
    return current ?? undefined
  }

  /**
   * Checks whether a frame is from one of our own scripts
   * (which will have a filename ending in .js or .mjs)
   * or is from an inline script, which will have a 'filename'
   * that's just the URL of the page.
   *
   * @returns `true` when the filename's path ends in `.js` or
   *   `.mjs`, or when the filename is not a parseable URL (ie err on the side of reporting).
   */
  const isFromOurScriptFile = (frame: RollbarFrame): boolean => {
    try {
      return /\.m?js$/.test(new URL(frame.filename).pathname)
    } catch {
      return true
    }
  }

  /**
   * This blocks exceptions thrown by Google Translate in
   * Google Chrome on iOS (and the Google app on iOS).
   *
   * @returns `true` when the item is almost certainly a stack overflow from
   *   Google Translate and Rollbar should ignore it.
   */
  const isStackOverflowFromiOSChromeTranslate = (item: RollbarItem | undefined): boolean => {
    const body = item?.data?.body
    const trace = body?.trace ?? body?.trace_chain?.[0]
    const exception = trace?.exception
    if (exception?.class !== "RangeError") return false
    if (!/Maximum call stack size exceeded/.test(exception.message ?? "")) return false

    // Confirm that the user agent is Chrome on iOS (CriOS) or
    // the Google iOS app (GSA)
    if (!/\b(CriOS|GSA)\//.test(navigator.userAgent)) return false

    const pageClasses = document.documentElement?.classList
    const pageIsGoogleTranslated = pageClasses?.contains("translated-ltr") || pageClasses?.contains("translated-rtl")
    if (!pageIsGoogleTranslated) return false

    const frames = trace?.frames ?? []
    return frames.length > 0 && !frames.some(isFromOurScriptFile)
  }

  window.__rollbarFilters = {
    ignoredMessages: [
      "ResizeObserver loop completed with undelivered notifications.",
      /Unable to preload CSS/i,
      // Globals that Firefox for iOS, crypto-wallet browsers and iOS in-app browsers inject; our code never uses them.
      /__firefox__/,
      /window\.ethereum/,
      /window\.webkit\.messageHandlers/,
    ],
    // Our clients set no timeout, so ECONNABORTED is the browser abandoning the request, like ERR_CANCELED.
    // 401, 403 and 429 are auth and rate limiting doing their job.
    checkIgnore(_isUncaught, args, item) {
      const failure = axiosErrorOf(Array.from(args ?? []).find((arg) => arg instanceof Error))
      return (
        isStackOverflowFromiOSChromeTranslate(item) ||
        ["ERR_CANCELED", "ECONNABORTED"].includes(failure?.code ?? "") ||
        [401, 403, 429].includes(failure?.response?.status ?? 0)
      )
    },
    // 400, 404, 405, 406 and 415 mean our client and server disagree, so they stay errors.
    transform(payload, item) {
      const failure = axiosErrorOf(item?.err)
      const status = failure?.response?.status
      const clientError = status != null && status >= 400 && status < 500 && ![400, 404, 405, 406, 415].includes(status)
      if (failure?.code === "ERR_NETWORK" || clientError) payload.level = "warning"

      const extra = payload.body?.trace?.extra ?? payload.body?.message?.extra
      if (extra?.customJs) {
        payload.custom ??= {}
        payload.custom.customJs = extra.customJs
        payload.custom.storeId = extra.storeId
      }
    },
  }
}

type RollbarAxiosError = {
  isAxiosError?: boolean
  code?: string
  response?: { status: number }
  cause?: unknown
}

type RollbarFrame = { filename: string }

type RollbarTrace = {
  exception?: { class?: string; message?: string }
  frames?: Array<RollbarFrame>
}

type RollbarItem = {
  data?: { body?: { trace?: RollbarTrace; trace_chain?: Array<RollbarTrace> } }
}

type RollbarPayload = {
  level?: string
  body?: {
    trace?: { extra?: Record<string, unknown> }
    message?: { extra?: Record<string, unknown> }
  }
  custom?: Record<string, unknown>
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- merges into the global Window; this file is a classic script
interface Window {
  __rollbarFilters: {
    ignoredMessages: Array<string | RegExp>
    checkIgnore: (isUncaught: boolean, args: ArrayLike<unknown> | undefined, item?: RollbarItem) => boolean
    transform: (payload: RollbarPayload, item?: { err?: unknown }) => void
  }
}
